import { useQuery } from '@tanstack/react-query';
import { LocationTrick } from '../models/locationTrick';
import LocationService from '../services/location.service';
import { getAdjustedTiming, QueryTiming } from './useQueryConfig';
import { CapacitorMapboxSearch } from 'capacitor-mapbox-search-plugin';

// Array of famous ski/snowboard mountains around the world (100 resorts)
const skiMountains: Array<{ name: string; latitude: number; longitude: number }> = [
    // Europe - Alps
    { name: "Whistler Blackcomb, Canada", latitude: 50.1163, longitude: -122.9574 },
    { name: "Chamonix, France", latitude: 45.9237, longitude: 6.8694 },
    { name: "St. Anton am Arlberg, Austria", latitude: 47.1271, longitude: 10.2640 },
    { name: "Zermatt, Switzerland", latitude: 45.9763, longitude: 7.6586 },
    { name: "Val d'Isère, France", latitude: 45.4488, longitude: 6.9808 },
    { name: "Verbier, Switzerland", latitude: 46.0964, longitude: 7.2280 },
    { name: "Courchevel, France", latitude: 45.4146, longitude: 6.6342 },
    { name: "St. Moritz, Switzerland", latitude: 46.4908, longitude: 9.8355 },
    { name: "Davos, Switzerland", latitude: 46.8043, longitude: 9.8307 },
    { name: "Kitzbühel, Austria", latitude: 47.4466, longitude: 12.3932 },
    { name: "Innsbruck, Austria", latitude: 47.2692, longitude: 11.4041 },
    { name: "Bad Gastein, Austria", latitude: 47.1145, longitude: 13.1344 },
    { name: "Sölden, Austria", latitude: 46.9692, longitude: 11.0041 },
    { name: "Méribel, France", latitude: 45.4008, longitude: 6.5672 },
    { name: "Val Thorens, France", latitude: 45.2969, longitude: 6.5803 },
    { name: "La Plagne, France", latitude: 45.5129, longitude: 6.6772 },
    { name: "Les Arcs, France", latitude: 45.5790, longitude: 6.7703 },
    { name: "Tignes, France", latitude: 45.4672, longitude: 6.9064 },
    { name: "Alpe d'Huez, France", latitude: 45.0908, longitude: 6.0672 },
    { name: "Val Gardena, Italy", latitude: 46.5569, longitude: 11.6758 },
    { name: "Cortina d'Ampezzo, Italy", latitude: 46.5407, longitude: 12.1357 },
    { name: "Madonna di Campiglio, Italy", latitude: 46.2272, longitude: 10.8272 },
    { name: "Livigno, Italy", latitude: 46.5368, longitude: 10.1344 },
    { name: "Cervinia, Italy", latitude: 45.9313, longitude: 7.6303 },
    { name: "Saas-Fee, Switzerland", latitude: 46.1086, longitude: 7.9286 },
    { name: "Engelberg, Switzerland", latitude: 46.8236, longitude: 8.4086 },
    { name: "Andermatt, Switzerland", latitude: 46.6369, longitude: 8.5944 },
    { name: "Garmisch-Partenkirchen, Germany", latitude: 47.4922, longitude: 11.0955 },

    // Europe - Other
    { name: "Åre, Sweden", latitude: 63.3989, longitude: 13.0819 },
    { name: "Riksgränsen, Sweden", latitude: 68.4267, longitude: 18.1267 },
    { name: "Hemsedal, Norway", latitude: 60.8633, longitude: 8.5500 },
    { name: "Lillehammer, Norway", latitude: 61.1153, longitude: 10.4662 },
    { name: "Trysil, Norway", latitude: 61.3167, longitude: 12.2667 },
    { name: "Bansko, Bulgaria", latitude: 41.8311, longitude: 23.4925 },
    { name: "Borovets, Bulgaria", latitude: 42.2536, longitude: 23.5897 },
    { name: "Zakopane, Poland", latitude: 49.2992, longitude: 19.9496 },
    { name: "Jasná, Slovakia", latitude: 48.9500, longitude: 19.5833 },
    { name: "Poiana Brasov, Romania", latitude: 45.6167, longitude: 25.5833 },
    { name: "Kopaonik, Serbia", latitude: 43.2911, longitude: 20.8147 },
    { name: "Gudauri, Georgia", latitude: 42.4781, longitude: 44.4736 },

    // North America - USA
    { name: "Aspen, Colorado, USA", latitude: 39.1911, longitude: -106.8175 },
    { name: "Vail, Colorado, USA", latitude: 39.6403, longitude: -106.3742 },
    { name: "Jackson Hole, Wyoming, USA", latitude: 43.5823, longitude: -110.8270 },
    { name: "Park City, Utah, USA", latitude: 40.6461, longitude: -111.4980 },
    { name: "Steamboat Springs, Colorado, USA", latitude: 40.4850, longitude: -106.8317 },
    { name: "Telluride, Colorado, USA", latitude: 37.9375, longitude: -107.8123 },
    { name: "Breckenridge, Colorado, USA", latitude: 39.4817, longitude: -106.0384 },
    { name: "Keystone, Colorado, USA", latitude: 39.6042, longitude: -105.9342 },
    { name: "Copper Mountain, Colorado, USA", latitude: 39.5019, longitude: -106.1506 },
    { name: "Winter Park, Colorado, USA", latitude: 39.8917, longitude: -105.7644 },
    { name: "Squaw Valley, California, USA", latitude: 39.1969, longitude: -120.2356 },
    { name: "Heavenly, California, USA", latitude: 38.9342, longitude: -119.9411 },
    { name: "Mammoth Mountain, California, USA", latitude: 37.6308, longitude: -119.0325 },
    { name: "Big Bear, California, USA", latitude: 34.2439, longitude: -116.9114 },
    { name: "Mount Bachelor, Oregon, USA", latitude: 43.9792, longitude: -121.6878 },
    { name: "Crystal Mountain, Washington, USA", latitude: 46.9297, longitude: -121.4731 },
    { name: "Stowe, Vermont, USA", latitude: 44.4653, longitude: -72.7875 },
    { name: "Killington, Vermont, USA", latitude: 43.6042, longitude: -72.8217 },
    { name: "Whiteface, New York, USA", latitude: 44.3653, longitude: -73.9022 },

    // North America - Canada
    { name: "Revelstoke, Canada", latitude: 50.9981, longitude: -118.2060 },
    { name: "Lake Louise, Canada", latitude: 51.4253, longitude: -116.1772 },
    { name: "Sunshine Village, Canada", latitude: 51.1053, longitude: -115.7661 },
    { name: "Fernie, Canada", latitude: 49.5047, longitude: -115.0628 },
    { name: "Big White, Canada", latitude: 49.7308, longitude: -118.9481 },
    { name: "Sun Peaks, Canada", latitude: 50.8847, longitude: -119.8842 },
    { name: "Mont Tremblant, Canada", latitude: 46.2094, longitude: -74.5903 },
    { name: "Blue Mountain, Canada", latitude: 44.5047, longitude: -80.3089 },
    { name: "Banff, Canada", latitude: 51.1784, longitude: -115.5708 },

    // Asia
    { name: "Niseko, Japan", latitude: 42.8048, longitude: 140.6874 },
    { name: "Hakuba, Japan", latitude: 36.7000, longitude: 137.8333 },
    { name: "Rusutsu, Japan", latitude: 42.7333, longitude: 140.8833 },
    { name: "Appi Kogen, Japan", latitude: 39.9333, longitude: 141.0333 },
    { name: "Zao, Japan", latitude: 38.1500, longitude: 140.4333 },
    { name: "Yongpyong, South Korea", latitude: 37.6350, longitude: 128.6808 },
    { name: "Phoenix Park, South Korea", latitude: 37.5808, longitude: 128.3208 },
    { name: "Gulmarg, India", latitude: 34.0482, longitude: 74.3803 },
    { name: "Auli, India", latitude: 30.5239, longitude: 79.5644 },
    { name: "Shymbulak, Kazakhstan", latitude: 43.1333, longitude: 77.0667 },
    { name: "Chimbulak, Kazakhstan", latitude: 43.1333, longitude: 77.0667 },

    // South America
    { name: "La Paz, Bolivia (Chacaltaya)", latitude: -16.3500, longitude: -68.1315 },
    { name: "Portillo, Chile", latitude: -32.8394, longitude: -70.1394 },
    { name: "Valle Nevado, Chile", latitude: -33.3500, longitude: -70.2667 },
    { name: "La Parva, Chile", latitude: -33.2833, longitude: -70.2833 },
    { name: "El Colorado, Chile", latitude: -33.3500, longitude: -70.3000 },
    { name: "Las Leñas, Argentina", latitude: -35.1167, longitude: -70.0333 },
    { name: "Cerro Catedral, Argentina", latitude: -41.1500, longitude: -71.4000 },
    { name: "Cerro Castor, Argentina", latitude: -54.7833, longitude: -68.1333 },
    { name: "Chapelco, Argentina", latitude: -40.1667, longitude: -71.2167 },

    // Australia & New Zealand
    { name: "Thredbo, Australia", latitude: -36.5008, longitude: 148.3078 },
    { name: "Perisher, Australia", latitude: -36.4089, longitude: 148.4039 },
    { name: "Falls Creek, Australia", latitude: -36.8622, longitude: 147.2831 },
    { name: "Mount Hotham, Australia", latitude: -36.9833, longitude: 147.1333 },
    { name: "Mount Buller, Australia", latitude: -37.1333, longitude: 146.4333 },
    { name: "Queenstown (The Remarkables), New Zealand", latitude: -45.0311, longitude: 168.7436 },
    { name: "Coronet Peak, New Zealand", latitude: -44.9833, longitude: 168.8167 },
    { name: "Mount Hutt, New Zealand", latitude: -43.5167, longitude: 171.6333 },
    { name: "Treble Cone, New Zealand", latitude: -44.7167, longitude: 169.1500 },
    { name: "Cardrona, New Zealand", latitude: -44.8833, longitude: 168.9500 },

    // Africa
    { name: "Oukaïmeden, Morocco", latitude: 31.2061, longitude: -7.8683 },
    { name: "Ifrane, Morocco", latitude: 33.5228, longitude: -5.1106 },
    { name: "Tiffindell, South Africa", latitude: -30.1833, longitude: 28.2167 },

    // Middle East
    { name: "Mount Hermon, Israel", latitude: 33.4167, longitude: 35.8500 },
    { name: "Mzaar, Lebanon", latitude: 34.0667, longitude: 35.8167 },
    { name: "Dizin, Iran", latitude: 36.0667, longitude: 51.4167 },
    { name: "Tochal, Iran", latitude: 35.7833, longitude: 51.4000 },

    // Additional European Gems
    { name: "Saariselkä, Finland", latitude: 68.4167, longitude: 27.4167 },
    { name: "Levi, Finland", latitude: 67.8000, longitude: 24.8000 },
    { name: "Ruka, Finland", latitude: 66.1667, longitude: 29.1333 },
    { name: "Vogel, Slovenia", latitude: 46.2833, longitude: 13.8500 },
    { name: "Kranjska Gora, Slovenia", latitude: 46.4833, longitude: 13.7833 }
];

// Function to get random ski mountain coordinates
export const getRandomSkiMountainCoordinates = (): { name: string; latitude: number; longitude: number } => {
    const randomIndex = Math.floor(Math.random() * skiMountains.length);
    const selectedMountain = skiMountains[randomIndex];
    console.log(`Selected random ski mountain: ${selectedMountain.name} (${selectedMountain.latitude}, ${selectedMountain.longitude})`);
    return selectedMountain;
};

export const useTrickLocation = (trickId: string, enabled: boolean = true) => {
    return useQuery({
        queryKey: ['trickLocation', trickId],
        queryFn: async (): Promise<LocationTrick | null> => {
            if (!trickId || trickId === "") {
                return null;
            }

            try {
                const response = await LocationService.getTrickLocation(trickId);

                // Return null if response is empty or doesn't have required data
                if (!response || Object.keys(response).length === 0 || response.name === undefined) {
                    return null;
                }

                return response;
            } catch (error) {
                console.error('Failed to fetch trick location:', error);
                return null;
            }
        },
        enabled: enabled && !!trickId && trickId !== "",
        ...getAdjustedTiming(QueryTiming.LOW_FREQUENCY),
    });
};


// Resolve coordinates for a location (abstracted logic)
export const resolveLocationCoordinates = async (location: string, trickLocationData: any): Promise<{ lat: number; lng: number }> => {
    // Try to use React Query data first
    if (trickLocationData && trickLocationData.name !== undefined) {
        const lat = trickLocationData.lat ? trickLocationData.lat : trickLocationData.google_lat;
        const lng = trickLocationData.long ? trickLocationData.long : trickLocationData.google_long;
        return { lat, lng };
    } else {
        // Fallback to Places API search
        try {
            const res = await LocationService.getLocationFromTextPlacesAPI(location);
            if (res) {
                const searched_location = res.result.geometry.location;
                const lat = parseFloat(searched_location.lat);
                const lng = parseFloat(searched_location.lng);
                return { lat, lng };
            } else {
                // Default fallback coordinates - use random ski mountain
                const randomMountain = getRandomSkiMountainCoordinates();
                return { lat: randomMountain.latitude, lng: randomMountain.longitude };
            }
        } catch (error) {
            console.error('Failed to geocode location:', error);
            // Default fallback coordinates - use random ski mountain
            const randomMountain = getRandomSkiMountainCoordinates();
            return { lat: randomMountain.latitude, lng: randomMountain.longitude };
        }
    }
};

// Handle opening native map for Capacitor apps
export const openNativeMapWithLocation = async (location: string, trickLocationData: any, thumbnail: string) => {
    const { lat, lng } = await resolveLocationCoordinates(location, trickLocationData);

    // Open the native map
    try {
        const mapThumbUrl = thumbnail.replace("d3btq01zzysv3d.cloudfront.net", "d2xrbrtg3kyq6g.cloudfront.net");

        await CapacitorMapboxSearch.openMap({
            location: {
                latitude: lat,
                longitude: lng
            },
            thumbnail: mapThumbUrl
        });

        console.log('Native Mapbox map opened successfully');
    } catch (error) {
        console.error('Failed to open native map:', error);
    }
};
